import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
  Alert,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { usajiliApi } from '@services/api/usajili';
import { USAJILIUploadPreviewResponse } from '@types';

interface Props {
  open: boolean;
  onClose: () => void;
  onDone: () => void;
}

export const USAJILIBulkUploadDialog: React.FC<Props> = ({ open, onClose, onDone }) => {
  const qc = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<USAJILIUploadPreviewResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<'upsert' | 'replace'>('upsert');
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [doneMsg, setDoneMsg] = useState<string | null>(null);

  const reset = () => {
    setFile(null);
    setPreview(null);
    setLoading(false);
    setError(null);
    setMode('upsert');
    setDoneMsg(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const onPickFile = async (f: File | null) => {
    if (!f) return;
    setFile(f);
    setError(null);
    setPreview(null);
    setDoneMsg(null);
    setLoading(true);
    try {
      const res = await usajiliApi.uploadPreview(f);
      setPreview(res);
    } catch (e: any) {
      setError(e?.error?.message || e?.message || 'Preview failed');
    } finally {
      setLoading(false);
    }
  };

  const applyMut = useMutation({
    mutationFn: ({ file, mode }: { file: File; mode: 'upsert' | 'replace' }) =>
      usajiliApi.uploadApply(file, mode),
    onSuccess: (r) => {
      setDoneMsg(
        `Upload applied: ${r.total.toLocaleString()} rows — ${r.inserted.toLocaleString()} new, ` +
          `${r.updated.toLocaleString()} updated. The dashboard has been refreshed.`
      );
      void qc.invalidateQueries();
      onDone();
    },
    onError: (e: any) => setError(e?.error?.message || e?.message || 'Apply failed'),
  });

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>Upload updated Daily data (Excel or CSV)</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Box>
            <input
              ref={inputRef}
              type="file"
              accept=".xlsx,.csv"
              hidden
              onChange={(e) => onPickFile(e.target.files?.[0] || null)}
            />
            <Button
              variant="contained"
              startIcon={<UploadFileIcon />}
              onClick={() => inputRef.current?.click()}
            >
              {file ? 'Pick another file…' : 'Choose Excel (.xlsx) or CSV file'}
            </Button>
            {file && (
              <Chip
                label={file.name}
                size="small"
                sx={{ ml: 1 }}
                color="primary"
                variant="outlined"
              />
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="upload-mode-lbl">Write mode</InputLabel>
              <Select
                labelId="upload-mode-lbl"
                label="Write mode"
                value={mode}
                onChange={(e) => setMode(e.target.value as any)}
              >
                <MenuItem value="upsert">
                  Upsert — match by (date, county), update existing / insert new
                </MenuItem>
                <MenuItem value="replace">
                  Replace — delete all rows and insert uploaded rows
                </MenuItem>
              </Select>
            </FormControl>
            {mode === 'replace' && (
              <Alert severity="warning">
                Replace mode will <b>truncate the entire usajili_daily_data table</b> and
                re-populate it with the uploaded rows. Use this only for authoritative full
                replacement files.
              </Alert>
            )}
          </Box>

          {loading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={20} />
              <span>Parsing and validating…</span>
            </Box>
          )}

          {error && <Alert severity="error">{error}</Alert>}

          {doneMsg && <Alert severity="success">{doneMsg}</Alert>}

          {preview && !loading && (
            <Stack spacing={1}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <Chip
                  color="success"
                  variant="outlined"
                  icon={<CheckCircleIcon />}
                  label={`${preview.valid_count.toLocaleString()} valid rows`}
                />
                <Chip
                  color={preview.error_count > 0 ? 'error' : 'default'}
                  variant="outlined"
                  icon={<ErrorIcon />}
                  label={`${preview.error_count.toLocaleString()} errors`}
                />
              </Box>

              <Paper
                sx={{
                  width: '100%',
                  overflow: 'auto',
                  maxHeight: 340,
                  border: preview.can_apply
                    ? '1px solid #16A34A'
                    : '1px solid #DC2626',
                }}
              >
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, bgcolor: 'background.default' }}>#</TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: 'background.default' }}>DATE</TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: 'background.default' }}>COUNTY</TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontWeight: 700, bgcolor: 'background.default' }}
                      >
                        NEW IDS
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontWeight: 700, bgcolor: 'background.default' }}
                      >
                        ID REPLACEMENTS
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontWeight: 700, bgcolor: 'background.default' }}
                      >
                        VOTERS REGISTERED
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: 'background.default' }}>
                        STATUS
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {preview.preview_rows.map((r) => (
                      <TableRow
                        key={r.row}
                        sx={r.error ? { bgcolor: '#FEF2F2' } : {}}
                      >
                        <TableCell sx={{ color: '#666' }}>{r.row}</TableCell>
                        <TableCell>{r.date}</TableCell>
                        <TableCell>{r.county}</TableCell>
                        <TableCell align="right">{r.new_ids ?? '-'}</TableCell>
                        <TableCell align="right">{r.id_replacements ?? '-'}</TableCell>
                        <TableCell align="right">
                          {r.voters_registered ?? '-'}
                        </TableCell>
                        <TableCell>
                          {r.error ? (
                            <Chip
                              size="small"
                              color="error"
                              variant="outlined"
                              label={r.error}
                            />
                          ) : (
                            <Chip
                              size="small"
                              color="success"
                              variant="outlined"
                              label="OK"
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
              {preview.preview_rows.length >= 500 && (
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Preview capped at 500 rows; validation covers the full file.
                </Typography>
              )}
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
        <Button
          variant="contained"
          color="primary"
          disabled={!preview?.can_apply || applyMut.isPending || !!doneMsg}
          onClick={() => file && preview?.can_apply && applyMut.mutate({ file, mode })}
        >
          {applyMut.isPending ? 'Applying…' : 'Apply upload'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
